import { Product } from '@@types'
import { useTranslation } from 'react-i18next'

import { usePopularProducts } from '@entities/Product'

import { useSearchHistoryStore } from '@features/search/model/useSearchStore'
import { useAddressStore } from '@features/shipping-methods'

import { ProductCard } from '@widgets/product-preview'

import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import ButtonClose from '@shared/ui/button-close'
import CardSkeleton from '@shared/ui/card-skeleton'
import Wrapper from '@shared/ui/wrapper'

import EmptyPopover from './empty-popover/EmptyPopover'

import styles from './styles.module.scss'


export interface SearchResultsPopoverProps {
  searchProducts: Product[];
  searchLoading: boolean;
  inModal?: boolean;
  isEmpty: boolean;
  hasResults: boolean;
  onClose: () => void;
  onSearch: () => void;
  onHistoryClick: (val: string) => void;
  onAddToCart: () => void;
}

export function SearchResultsPopover ({
  searchProducts,
  searchLoading,
  inModal,
  isEmpty,
  hasResults,
  onClose,
  onSearch,
  onHistoryClick,
  onAddToCart,
}: SearchResultsPopoverProps) {
  const { history, clearHistory } = useSearchHistoryStore()
  const { shopId } = useAddressStore()
  const { popularProducts } = usePopularProducts({
    shop_id: shopId,
    pager: {
      page: 1,
      limit: 4
    }
  })
  const { t: ts } = useTranslation('search')

  return (
    <Wrapper
      className={cn(styles.wrapper, {
        [styles.empty]: isEmpty,
        [styles.inModal]: inModal,
      })}
    >
      <div className={styles['search-top']}>
        {!!history.length && !isEmpty && (
          <div className={styles.history}>
            <h3 className={styles.title}>{ts('searchHistory')}</h3>
            <div className={styles.history__grid}>
              {history.map((h) => {
                return (
                  <button
                    className={cn('reset-button', styles.history__item)}
                    onClick={() => onHistoryClick(h)}
                    key={h}
                  >
                    {h}
                  </button>
                )
              })}
            </div>
            <button
              className={cn('reset-button', styles['history__clear'])}
              type="button"
              onClick={clearHistory}
            >
              {ts('clearHistory')}
            </button>
          </div>
        )}

        {!inModal && (
          <ButtonClose
            className={styles.close}
            theme="grey"
            onClick={onClose}
          />
        )}
      </div>

      {isEmpty && !searchLoading && <EmptyPopover onGoToCatalog={onClose} />}

      {popularProducts &&
        popularProducts.length > 0 &&
        !searchProducts &&
        !searchLoading &&
        !isEmpty && (
        <div className={styles.popular}>
          <h3 className={styles.title}>{ts('recommended')}</h3>
          <div className={styles.products}>
            {popularProducts.map((product) => (
              <ProductCard
                data={product}
                key={product?.id}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        </div>
      )}

      {hasResults && (
        <div className={styles.products}>
          {searchProducts.map((product) => (
            <ProductCard
              data={product}
              key={product?.id}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      )}

      {!hasResults && searchLoading && (
        <div className={styles.products}>
          {Array.from({ length: 4 }).map((_, index) => (
            <CardSkeleton key={index} />
          ))}
        </div>
      )}

      {hasResults && (
        <Button
          className={styles.root__results}
          theme="grey"
          onClick={() => onSearch()}
        >
          {ts('allResults')}
        </Button>
      )}
    </Wrapper>
  )
}
